import { computed, inject, Injectable, signal } from "@angular/core";
import { firstValueFrom, Subscription } from "rxjs";
import { HttpClient } from "@angular/common/http";
import { environment } from "../../../../environments/environment";
import { LiveProductionApiService } from "./live-production-api.service";
import { LiveProductionSocketService } from "./live-production-socket.service";
import {
  LineLiveCadence,
  LineLiveSnapshot,
  LiveProductionSnapshotPayload,
} from "../models/live-production.model";
import { Report } from "../../report/models/report.model";
import { buildCadenceFromReport } from "./measure-parser.utils";
import { ReportSocketService } from "../../report/services/report-socket.service ";
import { DateService } from "../../../interceptors/date.service";
/**
 * Intervalle entre deux cycles de refresh WebSocket.
 * Chaque cycle envoie un `refresh(reportId)` par ligne, espacé de
 * WS_STAGGER_MS pour laisser le serveur répondre à chacun.
 */
const WS_CYCLE_MS = 2_000;
/** Délai entre chaque refresh dans un même cycle (évite que le serveur
 *  ne réponde qu'au dernier). */
const WS_STAGGER_MS = 150;
/** Considère la trame "ancienne" si > 5 minutes. */
const DEVICE_OFFLINE_THRESHOLD_MS = 5 * 60_000;
/** Garde les 60 dernières trames pour le spark line. */
const HISTORY_LIMIT = 60;

/**
 * Service de supervision atelier live. Responsabilités :
 *  - écouter le canal WS `/ws/live-production` qui pousse le snapshot
 *    agrégé toutes les 5 s (évite le polling REST et la barre de
 *    chargement clignotante)
 *  - pour chaque ligne avec `reportId`, écouter le canal WS Aristote
 *    `/device-ws/dashboard/` (events `state`) pour les mesures live
 *    (cadence instantanée, moyenne, durée sans arrêt, nb arrêts)
 *  - exposer un signal `lignes()` agrégeant snapshot + cadences live
 *
 * Usage : `connect()` au mount du widget / page, `disconnect()` au unmount.
 * Les souscriptions multiples sont mutualisées (subscriberCount).
 *
 * `refresh()` reste disponible (REST one-shot) pour les forcés manuels.
 */
@Injectable({ providedIn: "root" })
export class LiveProductionService {
  private api = inject(LiveProductionApiService);
  private http = inject(HttpClient);
  private socket = inject(ReportSocketService);
  private liveSocket = inject(LiveProductionSocketService);

  /** Snapshot REST courant (sans cadences). */
  private snapshotSig = signal<LiveProductionSnapshotPayload | null>(null);

  /** Cadences live indexées par `ligneId`. */
  private cadencesSig = signal<Map<string, LineLiveCadence>>(new Map());

  /** Reports Aristote complets indexés par `ligneId` (pour `vl-charts-combined`). */
  private reportsSig = signal<Map<string, Report>>(new Map());

  /** Indicateur de chargement initial. */
  loading = signal(false);

  /** Erreur de chargement (null si OK). */
  error = signal<string | null>(null);

  /** Timestamp ISO de la dernière mise à jour du snapshot. */
  lastUpdate = computed(() => this.snapshotSig()?.generatedAt ?? null);

  /**
   * Lignes enrichies : `deviceConnected` calculé depuis `cadence.lastTickAt`
   * (séparé de `state` pour distinguer l'état métier de l'état de connexion
   * de l'automate). Pour rétrocompat, on garde l'override `state =
   * DEVICE_OFFLINE` quand applicable — les consommers qui ne savent pas lire
   * `deviceConnected` continueront à voir cet état.
   */
  lignes = computed<LineLiveSnapshot[]>(() => {
    const snap = this.snapshotSig();
    if (!snap) return [];
    const cadenceMap = this.cadencesSig();
    return snap.lignes.map((ligne) => {
      const cadence = cadenceMap.get(ligne.ligneId);
      let deviceConnected: boolean | null;
      let state = ligne.state;

      if (!ligne.deviceId) {
        deviceConnected = null;
      } else if (
        cadence?.lastTickAt &&
        Date.now() - new Date(cadence.lastTickAt).getTime() >
          DEVICE_OFFLINE_THRESHOLD_MS
      ) {
        deviceConnected = false;
        state = "DEVICE_OFFLINE";
      } else if (cadence?.lastTickAt) {
        deviceConnected = true;
      } else {
        // Pas encore reçu de trame — on assume connecté tant qu'on n'a pas
        // dépassé le seuil. La card peut afficher un état "en attente" si besoin.
        deviceConnected = true;
      }

      return { ...ligne, state, deviceConnected };
    });
  });

  /** Récupère la cadence live d'une ligne (ou null). */
  cadenceFor(ligneId: string): LineLiveCadence | null {
    return this.cadencesSig().get(ligneId) ?? null;
  }

  /** Récupère le Report complet d'une ligne (utile pour `vl-charts-combined`). */
  reportFor(ligneId: string): Report | null {
    return this.reportsSig().get(ligneId) ?? null;
  }

  private wsCycleHandle: ReturnType<typeof setInterval> | null = null;
  private stateSub: Subscription | null = null;
  private snapshotWsSub: Subscription | null = null;
  private subscriberCount = 0;

  /**
   * Démarre la souscription :
   *  1) WebSocket snapshot live-production (push toutes 5 s, 1ᵉʳ snapshot
   *     immédiat à la souscription)
   *  2) Fetch HTTP initial pour charger device.inputs de chaque report
   *  3) WebSocket Aristote : écoute `state` + envoi `refresh(reportId)`
   *     séquentiel espacé de 150 ms par ligne (même pattern que
   *     dashboard-report, adapté au multi-report).
   *
   * Idempotent via subscriberCount.
   */
  async connect(): Promise<void> {
    this.subscriberCount++;
    if (this.snapshotWsSub) return;

    // 1) Canal WS pour le snapshot live-production
    this.snapshotWsSub = this.liveSocket.snapshot$.subscribe((payload) => {
      this.snapshotSig.set(payload);
      this.error.set(null);
      this.loading.set(false);
    });
    this.liveSocket.connect();
    this.loading.set(true);

    // 2) HTTP initial pour charger device.inputs (nécessaire au key lookup)
    void this.refreshMeasures();
    // 3) WebSocket Aristote pour les mises à jour temps réel
    this.startWebSocket();
  }

  disconnect(): void {
    this.subscriberCount = Math.max(0, this.subscriberCount - 1);
    if (this.subscriberCount > 0) return;
    this.snapshotWsSub?.unsubscribe();
    this.snapshotWsSub = null;
    this.liveSocket.disconnect();
    this.stopWebSocket();
  }

  /**
   * WebSocket temps réel — même pattern que dashboard-report :
   *  - Écoute les events `state` pour recevoir les mesures fraîches
   *  - Émet `refresh(reportId)` pour chaque ligne, **séquentiellement**
   *    espacé de 150ms pour que le serveur réponde à chacun (le serveur
   *    ne track qu'un refresh à la fois par socket).
   */
  private startWebSocket(): void {
    this.stopWebSocket();

    // 1) Écoute des events `state`
    this.stateSub = this.socket.stateMessage$.subscribe((resp: any) => {
      try {
        if (!resp) return;
        const result: { report: Report; currentState: any; downtimes: any[] } =
          typeof resp === "string" ? JSON.parse(resp) : resp;

        DateService.convertToDate(result);

        const snap = this.snapshotSig();
        if (!snap) return;
        const ligne = snap.lignes.find(
          (l) => l.reportId === result.report?.id,
        );
        if (!ligne) return;

        // Merger avec le report caché (conserve device.inputs du fetch initial)
        // Dédup `states` par timestamp : le serveur renvoie le même
        // `currentState` à chaque cycle de refresh (toutes les 2 s) tant que
        // l'automate n'a pas envoyé de nouvelle trame. Sans dédup, le tableau
        // `states` enfle (ex: ligne stale → 670 doublons en 22 min) et le
        // graphe `vl-charts-combined` (qui rebuild depuis `states`) accumule
        // ces doublons → courbes faussées et/ou points empilés à la même
        // abscisse. On compare au dernier état caché et on skip si même date.
        const cached = this.reportsSig().get(ligne.ligneId);
        const cachedStates = cached?.states ?? [];
        const lastCachedTs = cachedStates.length
          ? cachedStates[cachedStates.length - 1].date?.getTime?.() ?? 0
          : 0;
        const newTs = result.currentState?.date?.getTime?.() ?? 0;
        const nextStates = newTs > lastCachedTs
          ? [...cachedStates, result.currentState]
          : cachedStates;
        const mergedReport: Report = {
          ...(cached ?? ({} as Report)),
          ...result.report,
          currentState: result.currentState,
          downtimes: result.downtimes ?? cached?.downtimes ?? [],
          states: nextStates,
        };

        const previous = this.cadencesSig().get(ligne.ligneId);
        const cadence = buildCadenceFromReport(
          mergedReport,
          previous?.history ?? [],
        );

        this.cadencesSig.update((map) => {
          const next = new Map(map);
          next.set(ligne.ligneId, cadence);
          return next;
        });
        this.reportsSig.update((map) => {
          const next = new Map(map);
          next.set(ligne.ligneId, mergedReport);
          return next;
        });
      } catch (e) {
      }
    });

    // 2) Cycle de refresh séquentiel (un reportId à la fois, espacé)
    this.wsCycleHandle = setInterval(
      () => this.emitRefreshCycle(),
      WS_CYCLE_MS,
    );
    // Premier cycle immédiat
    this.emitRefreshCycle();
  }

  /**
   * Envoie `refresh(reportId)` pour chaque ligne avec report,
   * espacé de WS_STAGGER_MS entre chaque envoi.
   */
  private emitRefreshCycle(): void {
    const lignes = this.snapshotSig()?.lignes ?? [];
    const withReport = lignes.filter((l) => l.reportId);
    withReport.forEach((l, i) => {
      setTimeout(() => this.socket.refresh(l.reportId!), i * WS_STAGGER_MS);
    });
  }

  private stopWebSocket(): void {
    this.stateSub?.unsubscribe();
    this.stateSub = null;
    if (this.wsCycleHandle) {
      clearInterval(this.wsCycleHandle);
      this.wsCycleHandle = null;
    }
  }

  /** Force un refresh manuel (snapshot REST + mesures Aristote). */
  async refresh(): Promise<void> {
    this.loading.set(true);
    this.error.set(null);
    try {
      const payload = await firstValueFrom(this.api.snapshot());
      this.snapshotSig.set(payload);
    } catch (err) {
      console.error("[live-production] snapshot error", err);
      this.error.set("Impossible de charger le snapshot live");
    } finally {
      this.loading.set(false);
    }
  }

  /**
   * Récupère les mesures live depuis Aristote pour chaque ligne avec reportId.
   * Met à jour la map de cadences. Tolère les erreurs ligne par ligne.
   */
  async refreshMeasures(): Promise<void> {
    const lignes = this.snapshotSig()?.lignes ?? [];
    const reports = lignes.filter((l) => l.reportId);
    if (reports.length === 0) return;

    await Promise.allSettled(
      reports.map(async (l) => {
        try {
          const report = await firstValueFrom(
            this.http.get<Report>(
              `${environment.aristoteApi()}/report/${l.reportId}`,
            ),
          );
          const previous = this.cadencesSig().get(l.ligneId);
          const cadence = buildCadenceFromReport(
            report,
            previous?.history ?? [],
          );
          this.cadencesSig.update((map) => {
            const next = new Map(map);
            next.set(l.ligneId, cadence);
            return next;
          });
          // Stocke aussi le Report complet pour l'historique multi-séries
          // (cf. vl-charts-combined).
          this.reportsSig.update((map) => {
            const next = new Map(map);
            next.set(l.ligneId, report);
            return next;
          });
        } catch (e) {
          // ligne inaccessible (token, archive…) : on n'écrase pas l'ancien tick
        }
      }),
    );
  }

  /**
   * API publique pour pousser une trame manuellement (compat WS v2). Pas
   * utilisé directement en v1 mais conservé pour le branchement futur d'un
   * canal WS agrégé.
   */
  pushTick(
    ligneId: string,
    measure: Omit<LineLiveCadence, "history" | "lastTickAt" | "defauts" | "dureeMaxSansArretSec" | "tempsTotalFonctionnementSec"> & Partial<Pick<LineLiveCadence, "defauts" | "dureeMaxSansArretSec" | "tempsTotalFonctionnementSec">>,
    timestamp: string = new Date().toISOString(),
  ): void {
    this.cadencesSig.update((map) => {
      const next = new Map(map);
      const existing = next.get(ligneId);
      const history = existing?.history ?? [];
      const nextHistory =
        measure.cadenceInstantUH != null
          ? [...history, { ts: timestamp, value: measure.cadenceInstantUH }].slice(
              -HISTORY_LIMIT,
            )
          : history;
      next.set(ligneId, {
        defauts: { d1: null, d2: null, d3: null, d4: null },
        dureeMaxSansArretSec: null,
        tempsTotalFonctionnementSec: null,
        ...measure,
        history: nextHistory,
        lastTickAt: timestamp,
      });
      return next;
    });
  }

  /** Reset toutes les cadences (utile au logout / changement de tenant). */
  resetCadences(): void {
    this.cadencesSig.set(new Map());
  }
}
